/**
 * @fileoverview Atlas content loader
 * @module Atlas/lib/contentLoader
 *
 * Provides typed getters for Atlas content with validation and
 * referential integrity checks. All content is loaded from JSON
 * files in src/data/atlas/.
 */

import productsData from '../../../data/atlas/products.json';
import edgesData from '../../../data/atlas/edges.json';
import personasData from '../../../data/atlas/personas.json';
import frameworksData from '../../../data/atlas/frameworks.json';
import scenariosData from '../../../data/atlas/scenarios.json';
import capabilitiesData from '../../../data/atlas/capabilities.json';
import glossaryData from '../../../data/atlas/glossary.json';
import model3dData from '../../../data/atlas/model3d.json';
import diagramsData from '../../../data/atlas/diagrams.json';

// Build lookup maps for fast access
const productMap = new Map(productsData.products.map(p => [p.id, p]));
const personaMap = new Map(personasData.personas.map(p => [p.id, p]));
const frameworkMap = new Map(frameworksData.frameworks.map(f => [f.id, f]));
const capabilityMap = new Map(capabilitiesData.capabilities.map(c => [c.id, c]));
const diagramMap = new Map(diagramsData.diagrams.map(d => [d.id, d]));
// Case-insensitive glossary lookup map
const glossaryMap = new Map(glossaryData.terms.map(t => [t.term.toLowerCase(), t]));

/**
 * Get all products
 * @returns {import('./types').Product[]}
 */
export function getProducts() {
  return productsData.products;
}

/**
 * Get a product by ID
 * @param {string} id - Product ID
 * @returns {import('./types').Product|undefined}
 */
export function getProductById(id) {
  return productMap.get(id);
}

/**
 * Get products by pillar
 * @param {string} pillar - Pillar ID
 * @returns {import('./types').Product[]}
 */
export function getProductsByPillar(pillar) {
  return productsData.products.filter(p => p.pillar === pillar);
}

/**
 * Get products by capability
 * @param {string} capabilityId - Capability ID
 * @returns {import('./types').Product[]}
 */
export function getProductsByCapability(capabilityId) {
  return productsData.products.filter(p =>
    p.capabilities && p.capabilities.includes(capabilityId)
  );
}

/**
 * Get all edges (product relationships)
 * @returns {import('./types').Edge[]}
 */
export function getEdges() {
  return edgesData.edges;
}

/**
 * Get edges for a specific product
 * @param {string} productId - Product ID
 * @returns {import('./types').Edge[]}
 */
export function getEdgesForProduct(productId) {
  return edgesData.edges.filter(e =>
    e.source === productId || e.target === productId
  );
}

/**
 * Get edge types with descriptions
 * @returns {Object.<string, {label: string, description: string}>}
 */
export function getEdgeTypes() {
  return edgesData.edgeTypes;
}

/**
 * Get all personas
 * @returns {import('./types').Persona[]}
 */
export function getPersonas() {
  return personasData.personas;
}

/**
 * Get a persona by ID
 * @param {string} id - Persona ID
 * @returns {import('./types').Persona|undefined}
 */
export function getPersonaById(id) {
  return personaMap.get(id);
}

/**
 * Get all frameworks
 * @returns {import('./types').Framework[]}
 */
export function getFrameworks() {
  return frameworksData.frameworks;
}

/**
 * Get a framework by ID
 * @param {string} id - Framework ID
 * @returns {import('./types').Framework|undefined}
 */
export function getFrameworkById(id) {
  return frameworkMap.get(id);
}

/**
 * Get products mapped to a framework control
 * @param {string} frameworkId - Framework ID
 * @param {string} controlId - Control/function/pillar ID
 * @returns {import('./types').Product[]}
 */
export function getProductsByFrameworkControl(frameworkId, controlId) {
  return productsData.products.filter(p => {
    const mappings = p.frameworkMappings?.[frameworkId];
    return mappings && mappings.includes(controlId);
  });
}

/**
 * Get all use cases
 * @returns {import('./types').UseCase[]}
 */
export function getUseCases() {
  return scenariosData.useCases;
}

/**
 * Get all 30/60/90 day timelines
 * @returns {import('./types').Timeline[]}
 */
export function getTimelines() {
  return scenariosData.timelines;
}

/**
 * Get all objections and responses
 * @returns {import('./types').Objection[]}
 */
export function getObjections() {
  return scenariosData.objections;
}

/**
 * Get all capabilities
 * @returns {import('./types').Capability[]}
 */
export function getCapabilities() {
  return capabilitiesData.capabilities;
}

/**
 * Get a capability by ID
 * @param {string} id - Capability ID
 * @returns {import('./types').Capability|undefined}
 */
export function getCapabilityById(id) {
  return capabilityMap.get(id);
}

/**
 * Get capabilities by pillar
 * @param {string} pillar - Pillar ID
 * @returns {import('./types').Capability[]}
 */
export function getCapabilitiesByPillar(pillar) {
  return capabilitiesData.capabilities.filter(c => c.pillar === pillar);
}

/**
 * Get all glossary terms
 * @returns {import('./types').GlossaryTerm[]}
 */
export function getGlossaryTerms() {
  return glossaryData.terms;
}

/**
 * Search glossary terms
 * @param {string} query - Search query
 * @returns {import('./types').GlossaryTerm[]}
 */
export function searchGlossary(query) {
  const q = query.toLowerCase();
  return glossaryData.terms.filter(t =>
    t.term.toLowerCase().includes(q) ||
    t.definition.toLowerCase().includes(q)
  );
}

/**
 * Get a glossary term by name (case-insensitive)
 * @param {string} termName - The term name to look up
 * @returns {import('./types').GlossaryTerm|undefined}
 */
export function getGlossaryTerm(termName) {
  if (!termName) return undefined;
  return glossaryMap.get(termName.toLowerCase());
}

/**
 * Get 3D model axes
 * @returns {Object}
 */
export function getModel3dAxes() {
  return model3dData.axes;
}

/**
 * Get 3D node positions
 * @returns {import('./types').NodePosition[]}
 */
export function getNodePositions() {
  return model3dData.nodePositions;
}

/**
 * Get color groups for 3D visualization
 * @returns {Object.<string, import('./types').ColorGroup>}
 */
export function getColorGroups() {
  return model3dData.colorGroups;
}

/**
 * Get persona-specific message for a product
 * @param {string} productId - Product ID
 * @param {string} personaId - Persona ID
 * @returns {string|null}
 */
export function getPersonaMessage(productId, personaId) {
  const product = productMap.get(productId);
  if (!product?.personaMessages) return null;
  return product.personaMessages[personaId] || null;
}

/**
 * Run referential integrity checks
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateIntegrity() {
  const errors = [];

  // Known external/legacy integrations that don't need their own product entries
  const KNOWN_EXTERNAL_INTEGRATIONS = [
    'external_tools',
    'cicd',
    'panorama',       // Legacy management platform (being replaced by SCM)
    'globalprotect',  // Endpoint agent (part of Prisma Access bundle)
  ];

  // Check product integrations reference valid products
  productsData.products.forEach(product => {
    if (product.integrations) {
      product.integrations.forEach(intId => {
        if (!productMap.has(intId) && !KNOWN_EXTERNAL_INTEGRATIONS.includes(intId)) {
          errors.push(`Product ${product.id}: integration '${intId}' not found`);
        }
      });
    }
  });

  // Check edges reference valid products
  edgesData.edges.forEach(edge => {
    if (!productMap.has(edge.source)) {
      errors.push(`Edge: source '${edge.source}' not found`);
    }
    if (!productMap.has(edge.target)) {
      errors.push(`Edge: target '${edge.target}' not found`);
    }
  });

  // Check persona relevantProducts reference valid products
  personasData.personas.forEach(persona => {
    if (persona.relevantProducts) {
      persona.relevantProducts.forEach(prodId => {
        if (!productMap.has(prodId)) {
          errors.push(`Persona ${persona.id}: relevantProduct '${prodId}' not found`);
        }
      });
    }
  });

  // Check use case primaryProducts reference valid products
  scenariosData.useCases.forEach(uc => {
    if (uc.primaryProducts) {
      uc.primaryProducts.forEach(prodId => {
        if (!productMap.has(prodId)) {
          errors.push(`UseCase ${uc.id}: primaryProduct '${prodId}' not found`);
        }
      });
    }
  });

  // Check timeline products reference valid products
  scenariosData.timelines.forEach(tl => {
    if (!productMap.has(tl.product)) {
      errors.push(`Timeline ${tl.id}: product '${tl.product}' not found`);
    }
  });

  // Check glossary relatedProducts reference valid products
  glossaryData.terms.forEach(term => {
    if (term.relatedProducts) {
      term.relatedProducts.forEach(prodId => {
        if (!productMap.has(prodId)) {
          errors.push(`Glossary '${term.term}': relatedProduct '${prodId}' not found`);
        }
      });
    }
  });

  // Check model3d nodePositions reference valid products
  model3dData.nodePositions.forEach(pos => {
    if (!productMap.has(pos.productId)) {
      errors.push(`Model3d: nodePosition '${pos.productId}' not found`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get all diagrams
 * @returns {import('./types').Diagram[]}
 */
export function getDiagrams() {
  return diagramsData.diagrams;
}

/**
 * Get a diagram by ID
 * @param {string} id - Diagram ID
 * @returns {import('./types').Diagram|undefined}
 */
export function getDiagramById(id) {
  return diagramMap.get(id);
}

/**
 * Get diagrams by category
 * @param {string} category - Category ID (overview, framework, journey, capability, integration)
 * @returns {import('./types').Diagram[]}
 */
export function getDiagramsByCategory(category) {
  return diagramsData.diagrams.filter(d => d.category === category);
}

/**
 * Get diagram categories
 * @returns {Object.<string, {name: string, description: string}>}
 */
export function getDiagramCategories() {
  return diagramsData.categories;
}

/**
 * Get content metadata
 * @returns {{products: Object, edges: Object, personas: Object, frameworks: Object, scenarios: Object, capabilities: Object, glossary: Object, model3d: Object}}
 */
export function getContentMeta() {
  // Six of the eight content files carry a `meta` block; products.json and
  // model3d.json use the newer `$schema` / `version` / `lastUpdated` shape and
  // have none. Reading `.meta` blindly returned undefined for exactly those
  // two, so any consumer asking "what version is the product catalogue?" got
  // nothing back. Normalise instead of reporting the newer shape as missing.
  const metaOf = (data) => data.meta || {
    version: data.version,
    lastUpdated: data.lastUpdated,
    schema: data.$schema,
  };

  return {
    products: metaOf(productsData),
    edges: metaOf(edgesData),
    personas: metaOf(personasData),
    frameworks: metaOf(frameworksData),
    scenarios: metaOf(scenariosData),
    capabilities: metaOf(capabilitiesData),
    glossary: metaOf(glossaryData),
    model3d: metaOf(model3dData)
  };
}

/**
 * Convert numeric confidence (0-1) to label string
 * @param {number|string} confidence - Confidence value (0-1) or existing label
 * @returns {'High'|'Medium'|'Low'} Confidence label
 */
export function getConfidenceLabel(confidence) {
  // If already a string label, return it normalized
  if (typeof confidence === 'string') {
    const normalized = confidence.toLowerCase();
    if (normalized === 'high') return 'High';
    if (normalized === 'medium') return 'Medium';
    if (normalized === 'low') return 'Low';
    return 'Medium'; // Default for unknown strings
  }

  // Numeric value - convert to label
  if (typeof confidence === 'number') {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  }

  // Fallback for undefined/null
  return 'Medium';
}

/**
 * Confidence color mapping
 * Returns CSS variable name for confidence level
 * @param {number|string} confidence - Confidence value or label
 * @returns {string} CSS variable name
 */
export function getConfidenceColor(confidence) {
  const label = getConfidenceLabel(confidence);
  switch (label) {
    case 'High': return '--color-success';
    case 'Medium': return '--color-warning';
    case 'Low': return '--color-error';
    default: return '--color-text-muted';
  }
}

export default {
  getProducts,
  getProductById,
  getProductsByPillar,
  getProductsByCapability,
  getEdges,
  getEdgesForProduct,
  getEdgeTypes,
  getPersonas,
  getPersonaById,
  getFrameworks,
  getFrameworkById,
  getProductsByFrameworkControl,
  getUseCases,
  getTimelines,
  getObjections,
  getCapabilities,
  getCapabilityById,
  getCapabilitiesByPillar,
  getGlossaryTerms,
  getGlossaryTerm,
  searchGlossary,
  getModel3dAxes,
  getNodePositions,
  getColorGroups,
  getPersonaMessage,
  getDiagrams,
  getDiagramById,
  getDiagramsByCategory,
  getDiagramCategories,
  validateIntegrity,
  getContentMeta,
  getConfidenceLabel,
  getConfidenceColor
};
